import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const HelpCenterPage = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  const categories = [
    { number: "01", title: t('help.gettingStarted'), description: t('help.gettingStartedDesc'), color: "rose" },
    { number: "02", title: t('help.subscriptions'), description: t('help.subscriptionsDesc'), color: "emerald" },
    { number: "03", title: t('help.safetyPrivacy'), description: t('help.safetyPrivacyDesc'), color: "blue" },
    { number: "04", title: t('help.photoPrivacy'), description: t('help.photoPrivacyDesc'), color: "purple" },
    { number: "05", title: t('help.communication'), description: t('help.communicationDesc'), color: "amber" },
    { number: "06", title: t('help.accountSettings'), description: t('help.accountSettingsDesc'), color: "pink" },
  ];

  const faqs = [
    {
      category: t('help.catGettingStarted'),
      questions: [
        { q: t('help.faq1Q'), a: t('help.faq1A') },
        { q: t('help.faq2Q'), a: t('help.faq2A') },
        { q: t('help.faq3Q'), a: t('help.faq3A') }
      ]
    },
    {
      category: t('help.catSubscriptions'),
      questions: [
        { q: t('help.faq4Q'), a: t('help.faq4A') },
        { q: t('help.faq5Q'), a: t('help.faq5A') },
        { q: t('help.faq6Q'), a: t('help.faq6A') },
        { q: t('help.faq7Q'), a: t('help.faq7A') }
      ]
    },
    {
      category: t('help.catPhotoPrivacy'),
      questions: [
        { q: t('help.faq8Q'), a: t('help.faq8A') },
        { q: t('help.faq9Q'), a: t('help.faq9A') },
        { q: t('help.faq10Q'), a: t('help.faq10A') }
      ]
    },
    {
      category: t('help.catCommunication'),
      questions: [
        { q: t('help.faq11Q'), a: t('help.faq11A') },
        { q: t('help.faq12Q'), a: t('help.faq12A') },
        { q: t('help.faq13Q'), a: t('help.faq13A') }
      ]
    },
    {
      category: t('help.catSafety'),
      questions: [
        { q: t('help.faq14Q'), a: t('help.faq14A') },
        { q: t('help.faq15Q'), a: t('help.faq15A') },
        { q: t('help.faq16Q'), a: t('help.faq16A') }
      ]
    },
    {
      category: t('help.catAccount'),
      questions: [
        { q: t('help.faq17Q'), a: t('help.faq17A') },
        { q: t('help.faq18Q'), a: t('help.faq18A') },
        { q: t('help.faq19Q'), a: t('help.faq19A') }
      ]
    }
  ];

  const filteredFaqs = searchQuery 
    ? faqs.map(cat => ({
        ...cat,
        questions: cat.questions.filter(q => 
          q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
          q.a.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(cat => cat.questions.length > 0)
    : faqs;

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-amber-50/30">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-rose-100/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-36">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img src={LOGO_URL} alt="SingleSeen" className="h-36 w-auto" />
            </Link>
            <Link to="/" className="text-rose-600 hover:text-rose-700 font-medium">
              ← Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-40 pb-16 bg-gradient-to-r from-rose-500 to-pink-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-6">
            <span className="text-3xl text-white font-serif">?</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-white mb-4">Help Center</h1>
          <p className="text-rose-100 text-lg mb-8">Find answers to your questions about SingleSeen</p>
          
          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <Input
              type="text"
              placeholder="Search for help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="py-6 rounded-full border-white/30 bg-white/90 text-rose-900 placeholder-rose-400"
              data-testid="help-search"
            />
          </div>
        </div>
      </section>

      {/* Quick Categories */}
      <section className="py-12 -mt-8 relative z-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((cat, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 text-center border border-rose-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center bg-${cat.color}-100`}>
                  <span className={`text-xl font-light text-${cat.color}-500`}>{cat.number}</span>
                </div>
                <h3 className="font-semibold text-rose-900 text-sm mb-1">{cat.title}</h3>
                <p className="text-rose-500 text-xs">{cat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="font-serif text-2xl text-rose-900 mb-8 text-center">Frequently Asked Questions</h2>
          
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-rose-100">
              <p className="text-rose-600">No results found for "{searchQuery}"</p>
              <Button 
                variant="outline"
                onClick={() => setSearchQuery("")}
                className="mt-4 border-rose-300 text-rose-700"
              >
                Clear Search
              </Button>
            </div>
          ) : (
            <div className="space-y-8">
              {filteredFaqs.map((category, catIndex) => (
                <div key={catIndex} className="bg-white rounded-2xl border border-rose-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-rose-50 to-pink-50 px-6 py-4 border-b border-rose-100">
                    <h3 className="font-semibold text-rose-900">{category.category}</h3>
                  </div>
                  <div className="divide-y divide-rose-100">
                    {category.questions.map((faq, faqIndex) => {
                      const uniqueIndex = `${catIndex}-${faqIndex}`;
                      return (
                        <div key={faqIndex}>
                          <button
                            onClick={() => toggleFaq(uniqueIndex)}
                            className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-rose-50/50 transition-colors"
                            data-testid={`faq-${uniqueIndex}`}
                          >
                            <span className="font-medium text-rose-900 pr-4">{faq.q}</span>
                            <span className="text-rose-400 flex-shrink-0 text-xl font-light">
                              {openFaq === uniqueIndex ? "−" : "+"}
                            </span>
                          </button>
                          {openFaq === uniqueIndex && (
                            <div className="px-6 pb-4 text-rose-600 leading-relaxed">
                              {faq.a}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Still Need Help */}
      <section className="py-16 bg-gradient-to-r from-rose-50 to-pink-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-serif text-2xl text-rose-900 mb-4">Still Need Help?</h2>
          <p className="text-rose-600 mb-8">Our support team is here to assist you</p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full px-8 py-6">
                Contact Us
              </Button>
            </Link>
            <Button variant="outline" className="border-2 border-rose-300 text-rose-700 hover:bg-white rounded-full px-8 py-6">
              Live Chat
            </Button>
          </div>
          
          <p className="text-rose-500 text-sm mt-6">
            Email: <a href="mailto:Matchmaker@singleseen.com" className="text-rose-600 hover:text-rose-700 font-medium">Matchmaker@singleseen.com</a>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-rose-900 text-rose-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="SingleSeen" className="h-10 w-auto brightness-0 invert opacity-80" />
            </div>
            <div className="flex gap-8 text-sm">
              <Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link>
              <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            </div>
            <p className="text-sm">© 2025 SingleSeen. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HelpCenterPage;
